import axios from "axios";

// Backend base URL. In normal builds this is injected via REACT_APP_BACKEND_URL.
// If a deploy build ever ships without it baked in (production build missing the
// env var → empty string → API calls hit "undefined/api" and silently 404,
// which hides the Microsoft sign-in button and breaks login), fall back to the
// current origin. On Emergent the frontend + backend are served same-origin
// (the /api prefix routes to the backend), so window.location.origin is correct.
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "disciple_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Pass the browser's IANA timezone (e.g. "America/Chicago") on every
  // request so the backend can render times in the user's local zone
  // (Andre's MS365 snapshot, briefings, etc.) without relying on the MS
  // Graph mailboxSettings scope which isn't always granted.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) cfg.headers["X-User-Timezone"] = tz;
  } catch { /* old browser — backend has its own fallback */ }
  return cfg;
});

// Global 401 handler: stale/invalid token → drop it and bounce to /login.
// Prevents Uncaught axios rejections (CRA error overlay) on every authed call.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      const isAuthCall = err.config?.url?.includes("/auth/");
      if (!isAuthCall) {
        setToken(null);
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.replace("/login");
        }
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Stream messages via fetch (SSE). Returns an async generator yielding
 * { type, data } events from the server.
 */
export async function* streamMessage(conversationId, text) {
  const t = getToken();
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* ignore */ }
  const res = await fetch(`${API}/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(t ? { Authorization: `Bearer ${t}` } : {}),
      ...(tz ? { "X-User-Timezone": tz } : {}),
    },
    body: JSON.stringify({ text }),
  });
  if (!res.ok || !res.body) {
    throw new Error(`Stream failed: ${res.status}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    // SSE event boundary: blank line
    while ((idx = buffer.indexOf("\n\n")) >= 0) {
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let evType = "message";
      let evData = "";
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) evType = line.slice(6).trim();
        else if (line.startsWith("data:")) evData += line.slice(5).trim();
      }
      if (!evData) continue;
      try {
        const parsed = JSON.parse(evData);
        yield { type: evType, data: parsed };
      } catch {
        // ignore parse errors on partial events
      }
    }
  }
}
